import { faAngleDown, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import {
  ComponentProps,
  FC,
  ReactElement,
  ReactNode,
  forwardRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { Dropdown } from 'atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from 'atoms/icons';

const ButtonsWrapper = styled.div<{ fullWidth: boolean }>`
  position: relative;
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
`;

const StyledIcon = styled(FontAwesomeIcon)<{ $isMenuOpen: boolean }>`
  opacity: 0.5;
  margin-left: var(--unit);
  transition: transform 0.25s ease-out;
  transform: none;
  ${({ $isMenuOpen }) =>
    $isMenuOpen &&
    css`
      transform: rotate(-180deg);
    `};
`;

const MainButton = styled(Button)`
  display: flex;
  justify-content: space-between;
`;

const LabelWrapper = styled.span<{ filterSelected: boolean }>`
  overflow: hidden;
  ${({ filterSelected }) =>
    filterSelected &&
    css`
      margin-right: calc(3 * var(--unit));
    `};
`;

const CloseButton = styled(Button)`
  position: absolute;
  top: 0;
  right: 0;
`;

const CloseContent = styled.span`
  color: var(--c-neutral-1000);
`;

type Props = {
  buttonLabel: ReactNode;
  buttonSize?: 'small' | 'medium';
  children:
    | ReactElement
    | FC<React.PropsWithChildren<{ closeDropdown: () => void }>>;
  fullWidth?: boolean;
  filterSelected?: boolean;
  onClearFilter?: () => void;
  gap?: 0 | 4 | 8 | 16;
};

type TriggerProps = {
  expanded: boolean;
  buttonSize: NonNullable<Props['buttonSize']>;
} & Pick<
  Props,
  'buttonLabel' | 'fullWidth' | 'filterSelected' | 'onClearFilter'
> &
  Pick<ComponentProps<'button'>, 'onClick' | 'onMouseEnter' | 'onMouseLeave'>;

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  (
    {
      expanded,
      buttonLabel,
      buttonSize,
      fullWidth,
      filterSelected,
      onClearFilter,
      ...buttonProps
    },
    ref
  ) => {
    return (
      <ButtonsWrapper fullWidth={!!fullWidth}>
        <MainButton
          ref={ref}
          {...buttonProps}
          color="tertiary"
          size={buttonSize}
          fullWidth={fullWidth}
          disableDebounce
        >
          <LabelWrapper filterSelected={!!filterSelected}>
            {buttonLabel}
          </LabelWrapper>

          {!filterSelected && (
            <StyledIcon $isMenuOpen={expanded} icon={faAngleDown} />
          )}
        </MainButton>
        {filterSelected && (
          <CloseButton
            color="transparent"
            size={buttonSize}
            onClick={onClearFilter}
          >
            <CloseContent>
              <FontAwesomeIcon icon={faCircleXmark} />
            </CloseContent>
          </CloseButton>
        )}
      </ButtonsWrapper>
    );
  }
);

Trigger.displayName = 'Trigger';

export const FilterInDropdown = ({
  buttonLabel,
  buttonSize = 'small',
  fullWidth = false,
  filterSelected = false,
  onClearFilter = () => {},
  children,
  gap,
}: Props) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Dropdown
      fullWidth={fullWidth}
      align="right"
      label={props => (
        <Trigger
          expanded={expanded}
          buttonLabel={buttonLabel}
          buttonSize={buttonSize}
          fullWidth={fullWidth}
          filterSelected={filterSelected}
          onClearFilter={onClearFilter}
          {...props}
        />
      )}
      gap={gap || 16}
      onOpen={() => setExpanded(true)}
      onClose={() => setExpanded(false)}
    >
      {({ closeDropdown }) =>
        typeof children === 'function' ? children({ closeDropdown }) : children
      }
    </Dropdown>
  );
};
